import { AppRepository } from 'src/api/app_repository';
import { GraphqlClient } from 'src/api/graphql_client';
import { GraphqlMemberRepository } from 'src/api/graphql_member_repository';
import { MemberRepository } from 'src/api/member_repository';
import { OrderRepository } from 'src/api/order_repository';
import { OrderDetailDisplayer } from 'src/distribution_platforms/features/order_details';
import { RepoBackend, Region } from 'src/types/env';

/**
 * Interface for the instance factory.
 */
export interface InstanceFactory {
  readonly _repoBackend: RepoBackend;

  createAppRepository(graphqlClient?: GraphqlClient): AppRepository;
  createOrderRepository(
    _region: Region,
    graphqlClient?: GraphqlClient,
  ): OrderRepository;
  createMemberRepository(graphqlClient?: GraphqlClient): MemberRepository;
  createProstateBiopsyOrderDetailDisplayer(
    orderRepository: OrderRepository,
  ): OrderDetailDisplayer;
  createUploadWorker(): Worker;
}

/**
 * Base class for the instance factory. Contains common functionality for all
 * instance factories across all platforms.
 */
export abstract class BaseInstanceFactory {
  constructor(protected readonly _repoBackend: RepoBackend) {
    this._repoBackend = _repoBackend;
  }

  createMemberRepository(graphqlClient?: GraphqlClient): MemberRepository {
    if (this._repoBackend === RepoBackend.Real) {
      return new GraphqlMemberRepository(graphqlClient!);
    }

    throw new Error(
      'Member repository not implemented for this type of backend',
    );
  }

  createUploadWorker(): Worker {
    if (this._repoBackend === RepoBackend.Fake) {
      return new Worker(
        new URL('/src/utils/fake_upload_worker.js', import.meta.url),
        {
          type: 'module',
        },
      );
    } else if (this._repoBackend === RepoBackend.Real) {
      return new Worker(
        new URL('/src/utils/upload_worker.js', import.meta.url),
        {
          type: 'module',
        },
      );
    }

    throw new Error(`Invalid repository ${this._repoBackend}`);
  }
}
